import { AxiosInstance } from "axios";
import { useContext, useEffect, useState } from "react";
import httpClientFactory from "../config/httpConfig";
import { AppConfig } from "../context/context";
import {USERS_NUMBER} from "../utils/constants";

const USERS_ENDPOINT = "v1/users";
const ACCESSCODE = "AccessCode";
const USERS_SHORT = "brief-view";

interface IUserService {
  getUsers: () => Promise<any>;
  getAllUsersShort: () => Promise<any>;
  getUserRoles: (id: string) => Promise<any>;
  getUserRolesPerApp: (userId: string, appId: string) => Promise<any>;
  getUserSetting: (id: string) => Promise<any>;
  updateUserSetting: (id: string, userSetting: any) => Promise<any>;
  getUserLegal: (uid: string) => Promise<any>;
  patchUserLegal: (uid: string, data: any) => Promise<any>;
  getUserDuties: (uid: string) => Promise<any>;
  getUseAccessCode: (tenent: string, uid: string) => Promise<any>;
  postUseAccessCode: (tenent: string, settings: any) => Promise<any>;
  deleteAccessCode: (tenent: string, uid: string, code: string)=> Promise<any>
}

const USER_UPDATE_CONFLICTS = {
  USERNAME_CONFLICT: "The username is not unique.",
  EMAIL_CONFLICT: "The email address is not unique.",
  CODE_CONFLICT: "Access code is in use",
};

const UserService = (config: any): IUserService => {
  const http: AxiosInstance = httpClientFactory(config);
  return {
    getUsers: async (start = 0, size = 500) => {
      // size defaults to 500 because that is the max rows per page in the user list
      try {
        const data = await http.get(USERS_ENDPOINT, {
          params: { start, size },
        });
        return data;
      } catch (err) {
        return err;
      }
    },
    getAllUsersShort: async () => {
      return await http.get(`${USERS_ENDPOINT}/${USERS_SHORT}?size=${USERS_NUMBER.MAX_USERS}`);
    },
    getUserRoles: async (id) => {
      return await http.get(`${USERS_ENDPOINT}/${id}/roles`);
    },
    getUserRolesPerApp: async (userId, appId) => {
      return await http.get(`${USERS_ENDPOINT}/${userId}/${appId}/roles`);
    },
    getUserSetting: async (id) => {
      return await http.get(`${USERS_ENDPOINT}/${id}`).then((response: any) => {
        return { ...response };
      });
    },
    updateUserSetting: async (id: string, userSetting: any) => {
      try {
        return await http.put(`${USERS_ENDPOINT}/${id}`, userSetting);
      } catch (err) {
        return Promise.reject(enrichErrorWithConflicts(err));
      }
    },
    getUserLegal: async (uid: string) => {
      return await http.get(`${USERS_ENDPOINT}/${uid}/legal`);
    },
    patchUserLegal: async (uid: string, data: any) => {
      return await http.patch(`${USERS_ENDPOINT}/${uid}/legal`, data);
    },
    getUserDuties: (uid: string) => {
      return http.get(`${USERS_ENDPOINT}/${uid}/duties`);
    },
    getUseAccessCode: (tenent: string, uid: string) => {
      return http.get(`v1/Tenants/${tenent}/${ACCESSCODE}/${uid}`);
    },

    postUseAccessCode: async (tenent: string, settings: any) => {
      try {
        return await http.post(`v1/Tenants/${tenent}/${ACCESSCODE}`, settings);
      } catch (err) {
        return Promise.reject(enrichErrorWithConflicts(err));
      }
    },

    // ?allowmultiple=true

    deleteAccessCode: (tenent: string, uid: string, code: string) => {
      return http.delete(`v1/Tenants/${tenent}/${ACCESSCODE}/${uid}?code=${code}`);
    },
  };
};

export function enrichErrorWithConflicts(error: any) {
  const newError = {
    ...error,
    conflicts: {
      username: false,
      email: false,
      code: false,
    },
  };

  const { errors } = error?.response?.data;
  const singleError = error?.response?.data.error_description;

  if (errors?.length === 0) {
    return newError;
  }

  newError.conflicts.username =
    errors?.indexOf(USER_UPDATE_CONFLICTS.USERNAME_CONFLICT) > -1;
  newError.conflicts.email =
    errors?.indexOf(USER_UPDATE_CONFLICTS.EMAIL_CONFLICT) > -1;
  newError.conflicts.code = singleError === USER_UPDATE_CONFLICTS.CODE_CONFLICT;
  console.log({ newError });

  return newError;
}

export function useUserService() {
  const { config } = useContext(AppConfig);
  const [userService, setUserService] = useState<IUserService>(
    {} as IUserService
  );

  if (!config) {
    throw new Error("useServices hook must be used inside AppConfig context");
  }

  useEffect(() => {
    const { baseUrl, tenantId, token } = config;
    if (!baseUrl || !tenantId || !token) return;
    setUserService(UserService({ baseUrl, tenantId, token }));
  }, [config]);

  return {
    userService,
  };
}

export default UserService;
